/**
 * FinanzasEntidadesPage.jsx — Gestión de Entidades del Módulo de Finanzas para CIG
 * Ruta: /finanzas/entidades
 */
import { useState, useEffect, useCallback } from 'react';
import finanzasApi from '../../lib/finanzas_api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Building2, User, Pencil, Trash2, Search } from 'lucide-react';
import FinAppLayout from '../../components/FinAppLayout';

export default function FinanzasEntidadesPage() {
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ nombre: '', tipo: 'Empresa', rut: '', direccion: '', telefono: '' });
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEntidades = useCallback(async () => {
    try {
      const res = await finanzasApi.get('/entidades');
      setEntidades(res.data);
    } catch { toast.error('Error al cargar entidades'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEntidades(); }, [fetchEntidades]);

  const resetForm = () => { setForm({ nombre: '', tipo: 'Empresa', rut: '', direccion: '', telefono: '' }); setEditingId(null); };

  const openCreate = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (ent) => {
    setForm({ nombre: ent.nombre, tipo: ent.tipo, rut: ent.rut || '', direccion: ent.direccion || '', telefono: ent.telefono || '' });
    setEditingId(ent.id); setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editingId) { await finanzasApi.put(`/entidades/${editingId}`, form); toast.success('Entidad actualizada'); }
      else { await finanzasApi.post('/entidades', form); toast.success('Entidad creada'); }
      setDialogOpen(false); resetForm(); fetchEntidades();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const confirmDelete = (ent) => { setDeleteTarget(ent); setDeleteDialogOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await finanzasApi.delete(`/entidades/${deleteTarget.id}`);
      toast.success('Entidad eliminada'); setDeleteDialogOpen(false); setDeleteTarget(null); fetchEntidades();
    } catch { toast.error('Error al eliminar'); }
  };

  const filtered = entidades.filter(e =>
    e.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.rut && e.rut.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <FinAppLayout>
      <div data-testid="finanzas-entidades-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finanzas · Entidades
            </h1>
            <p className="mt-1 text-base text-slate-500">Gestiona tus empresas y personas</p>
          </div>
          <Button onClick={openCreate} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl shadow-sm gap-2 h-10" data-testid="create-entidad-btn">
            <Plus className="w-4 h-4" strokeWidth={2} /> Nueva Entidad
          </Button>
        </div>

        <div className="relative mb-6 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar entidad..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 bg-white border-slate-200 rounded-xl" data-testid="search-entidades-input" />
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-entidades">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-indigo-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">{searchTerm ? 'Sin resultados' : 'Sin entidades'}</h3>
            <p className="mt-2 text-sm text-slate-500">{searchTerm ? 'Intenta con otro término' : 'Crea tu primera empresa o persona'}</p>
          </div>
        ) : (
          <Card className="border border-slate-100 rounded-xl shadow-sm overflow-hidden" data-testid="entidades-table-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  {['Nombre', 'Tipo', 'RUT', 'Teléfono', 'Acciones'].map((h, i) => (
                    <TableHead key={h} className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${i === 4 ? 'text-right' : ''}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((ent) => (
                  <TableRow key={ent.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`entidad-row-${ent.id}`}>
                    <TableCell className="font-medium text-slate-700">{ent.nombre}</TableCell>
                    <TableCell>
                      <Badge className={`${ent.tipo === 'Empresa' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-violet-50 text-violet-700 border-violet-100'} border`}>
                        {ent.tipo === 'Empresa' ? <Building2 className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                        {ent.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 tabular-nums">{ent.rut || '—'}</TableCell>
                    <TableCell className="text-slate-500">{ent.telefono || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(ent)} className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`edit-entidad-${ent.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => confirmDelete(ent)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-entidad-${ent.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-xl" data-testid="entidad-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingId ? 'Editar Entidad' : 'Nueva Entidad'}</DialogTitle>
              <DialogDescription>{editingId ? 'Modifica los datos' : 'Agrega una empresa o persona'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre" required className="h-10 bg-slate-50/50" data-testid="entidad-nombre-input" />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="h-10 bg-slate-50/50" data-testid="entidad-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Empresa">Empresa</SelectItem>
                    <SelectItem value="Persona">Persona</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>RUT</Label>
                <Input value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} placeholder="12.345.678-9" className="h-10 bg-slate-50/50" data-testid="entidad-rut-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Dirección" className="h-10 bg-slate-50/50" />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+56 9..." className="h-10 bg-slate-50/50" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="entidad-save-btn">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-sm rounded-xl" data-testid="delete-entidad-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600">Eliminar Entidad</DialogTitle>
              <DialogDescription>
                ¿Eliminar <strong>{deleteTarget?.nombre}</strong>? Se eliminarán todas las cuentas y movimientos asociados.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete} className="rounded-xl" data-testid="delete-confirm-btn">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FinAppLayout>
  );
}
